import { fetchStaticJson } from './fetchStaticJson';
import pathRoutes from '../enums/pathRoutes';

function deepMerge(target, source) {
  const output = { ...target };
  Object.keys(source || {}).forEach((key) => {
    const t = output[key];
    const s = source[key];
    if (
      t && s &&
      typeof t === "object" &&
      typeof s === "object" &&
      !Array.isArray(t)
    ) {
      output[key] = deepMerge(t, s);
    } else {
      output[key] = s;
    }
  });
  return output;
}

export function resolveBotEntryForDomain(domainName, botName) {
  const { botsList = {} } = window.config || {};
  let project = botsList[domainName];
  let projectKey = domainName;

  if (!project || !project.bots) {
    const defaultProject = botsList.defaultProject;
    if (!defaultProject) return null;
    projectKey = defaultProject;
    project = botsList[defaultProject];
    if (!project || !project.bots) return null;
  }

  let resolvedBotName = botName;
  let bot = project.bots[botName];
  if (!bot) {
    const byUrlPath = Object.keys(project.bots).find((key) => {
      const candidate = project.bots[key];
      return getBotUrlPath(candidate) === normalizeDirectUrlPath(botName);
    });
    if (byUrlPath) {
      resolvedBotName = byUrlPath;
      bot = project.bots[byUrlPath];
    }
  }
  if (!bot) {
    resolvedBotName = project.defaultBot;
    bot = resolvedBotName ? project.bots[resolvedBotName] : null;
  }

  if (bot == null) return null;
  return { projectKey, project, resolvedBotName, bot };
}

export function normalizeDirectUrlPath(urlPathName) {
  return String(urlPathName || '').replace(/^\/+|\/+$/g, '').trim();
}

export function stripLegacyRegulatorySuffix(urlPathName) {
  const normalized = normalizeDirectUrlPath(urlPathName);
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length && parts[parts.length - 1].toLowerCase() === 'regulatory') {
    parts.pop();
    return parts.join('/');
  }
  return normalized;
}

function getBotUrlPath(bot) {
  if (bot && typeof bot === 'object' && typeof bot.urlPath === 'string') {
    return normalizeDirectUrlPath(bot.urlPath);
  }
  return '';
}

/** Public route segment: urlPath if set, else domainName/botName. */
export function getBotRoutePath(projectKey, botName, botOrUrlPath) {
  if (typeof botOrUrlPath === 'string') {
    const fromString = normalizeDirectUrlPath(botOrUrlPath);
    if (fromString) return fromString;
  } else {
    const fromBot = getBotUrlPath(botOrUrlPath);
    if (fromBot) return fromBot;
  }
  return [projectKey, botName].filter(Boolean).join('/');
}

function getBotConfigSourceUrl(bot) {
  if (typeof bot === 'string') return bot.trim();
  if (bot && typeof bot === 'object' && typeof bot.configUrl === 'string') {
    return bot.configUrl.trim();
  }
  return '';
}

function needsBotConfigLoad(bot) {
  if (typeof bot === 'string') return Boolean(bot.trim());
  if (!bot || typeof bot !== 'object') return false;
  const url = getBotConfigSourceUrl(bot);
  return Boolean(url) && bot.theme == null;
}

function preserveRegistryFields(loaded, bot, sourceUrl) {
  const next = { ...loaded };
  if (bot && typeof bot === 'object' && typeof bot.urlPath === 'string' && bot.urlPath.trim()) {
    next.urlPath = bot.urlPath;
  }
  if (sourceUrl) {
    next.configUrl = sourceUrl;
  }
  return next;
}

export function resolveBotEntryByUrlPath(urlPathName) {
  if (!urlPathName) return null;
  const normalized = normalizeDirectUrlPath(urlPathName);
  if (!normalized) return null;
  const { botsList = {} } = window.config || {};
  let found = null;
  Object.keys(botsList).forEach((key) => {
    if (found || key === 'defaultProject') return;
    const project = botsList[key];
    if (!project?.bots) return;
    Object.keys(project.bots).forEach((botName) => {
      if (found) return;
      const bot = project.bots[botName];
      if (getBotUrlPath(bot) === normalized) {
        found = { projectKey: key, project, resolvedBotName: botName, bot };
      }
    });
  });
  return found;
}

/** Resolve by urlPath first; fall back to domainName/botName path segments. */
export function resolveBotEntryFromRoutePath(routePathName) {
  const path = normalizeDirectUrlPath(routePathName);
  if (!path) return null;
  const byUrlPath = resolveBotEntryByUrlPath(path);
  if (byUrlPath) return byUrlPath;
  const parts = path.split('/').filter(Boolean);
  if (parts.length >= 2) {
    return resolveBotEntryForDomain(parts[0], parts.slice(1).join('/'));
  }
  return null;
}

// Returns the redirect path for root "/" - uses defaultProject when set, else first bot in list.
export function getDefaultRedirectPath() {
  const { botsList = {} } = window.config || {};
  const defaultProject = botsList.defaultProject;
  const project = defaultProject ? botsList[defaultProject] : null;
  if (project?.defaultBot) {
    const bot = project.bots?.[project.defaultBot];
    const routePath = getBotRoutePath(defaultProject, project.defaultBot, bot);
    return `/${pathRoutes.PORTAL}/${routePath}`;
  }
  const [first] = listBotsFromBotsList();
  if (!first) return null;
  return `/${pathRoutes.PORTAL}/${first.routePath}`;
}

export function listBotsFromBotsList() {
  const { botsList = {} } = window.config || {};
  const out = [];
  Object.keys(botsList).forEach((key) => {
    if (key === "defaultProject") return;
    const project = botsList[key];
    if (!project?.bots) return;
    Object.keys(project.bots).forEach((botName) => {
      const bot = project.bots[botName];
      const meta = typeof bot === 'object' && bot !== null ? bot : {};
      const routePath = getBotRoutePath(key, botName, bot);
      out.push({
        projectKey: key,
        botName,
        urlPath: meta.urlPath || '',
        routePath,
        displayName: meta.displayName || meta.botTitle || botName,
        subject: meta.subject || '',
        approvedBy: meta.approvedBy || '',
        iconUrl: meta.botIconUrl || '',
        activeInMyEdu: meta.activeInMyEdu === true,
      });
    });
  });
  return out;
}

export function getBotConfig(domainName, botName) {
  const wc = window.config || {};
  const { botsList, ...sharedRoot } = wc;

  const resolved = resolveBotEntryForDomain(domainName, botName);
  if (!resolved) return null;

  const { project, bot } = resolved;
  if (typeof bot === "string" || needsBotConfigLoad(bot)) {
    return null;
  }

  const projectSettings = { ...project };
  delete projectSettings.defaultBot;
  delete projectSettings.bots;

  let config = deepMerge({}, sharedRoot);
  config = deepMerge(config, projectSettings);
  config = deepMerge(config, bot);

  return config;
}

export async function loadBotConfig(url) {
  return fetchStaticJson(url);
}

async function hydrateBotEntry(entry) {
  if (!needsBotConfigLoad(entry.bot)) return;
  const url = getBotConfigSourceUrl(entry.bot);
  if (!url) {
    throw new Error('Unknown bot');
  }
  const loaded = await loadBotConfig(url);
  entry.project.bots[entry.resolvedBotName] = preserveRegistryFields(loaded, entry.bot, url);
}

export async function ensureBotConfigForRoute(domainName, botName) {
  const entry = resolveBotEntryForDomain(domainName, botName);
  if (!entry) {
    throw new Error('Unknown bot');
  }
  await hydrateBotEntry(entry);
  return getBotConfig(domainName, botName);
}

export async function ensureBotConfigForUrlPath(urlPathName) {
  return ensureBotConfigForRoutePath(urlPathName);
}

export async function ensureBotConfigForRoutePath(routePathName) {
  const entry = resolveBotEntryFromRoutePath(routePathName);
  if (!entry) {
    throw new Error('Unknown bot');
  }
  await hydrateBotEntry(entry);
  return getBotConfig(entry.projectKey, entry.resolvedBotName);
}

/** Always re-fetch bot JSON from bucket (admin designer). */
export async function reloadBotConfigForRoute(domainName, botName) {
  const entry = resolveBotEntryForDomain(domainName, botName);
  if (!entry) {
    throw new Error('Unknown bot');
  }

  let url = getBotConfigSourceUrl(entry.bot);
  if (!url) {
    const base = String(window.config?.baseApiUrl || '').replace(/\/$/, '');
    if (!base) {
      throw new Error('Missing baseApiUrl');
    }
    url = `${base}/bucketfiles/bots/${entry.projectKey}/${entry.resolvedBotName}_config.json`;
  }

  const loaded = await loadBotConfig(url);
  entry.project.bots[entry.resolvedBotName] = preserveRegistryFields(loaded, entry.bot, url);
  return getBotConfig(domainName, botName);
}

export async function loadBotsListFromBucket() {
  window.config = window.config || {};
  const url = window.config.botsListUrl;
  if (url) {
    try {
      const data = await fetchStaticJson(url);
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        window.config.botsList = data;
      }
    } catch {
      // keep botsList from global-settings.js
    }
  }
}